import { Fragment, useEffect, useRef, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, FileCsv, FilePdf, Trash, Plus, X, UploadSimple, Warning, ArrowBendUpLeft, Pencil } from "@phosphor-icons/react";

const METHODS = [
  { id: "contanti", label: "Contanti" },
  { id: "carta", label: "Carta" },
  { id: "bonifico", label: "Bonifico" },
  { id: "assegno", label: "Assegno" },
  { id: "finanziamento", label: "Finanziamento" },
  { id: "cash", label: "Cash" },
  { id: "bonus_docenti", label: "Bonus Docenti" },
];
const ENTRATA_ONLY_METHODS = ["bonus_docenti", "finanziamento"];
const methodsForType = (t) => METHODS.filter((m) => t === "entrata" || !ENTRATA_ONLY_METHODS.includes(m.id));
const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
const fmtShortDate = (d) => { const [y, m, g] = String(d || "").split("-"); return y && m && g ? `${g}-${m}-${y.slice(2)}` : d; };
const today = () => new Date().toISOString().slice(0, 10);
const inputCls = "w-full px-3.5 py-2.5 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-primary transition-colors duration-150";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5";
const methodLabel = (m) => METHODS.find((x) => x.id === m)?.label || m.replace("_", " ");

function SaleItemsEditor({ items, setItems, categories, testidPrefix }) {
  const update = (i, patch) => setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const total = items.reduce((s, it) => s + (parseFloat(String(it.amount).replace(",", ".")) || 0), 0);
  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-items-editor`}>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-[170px_1fr_130px_36px] gap-2 items-center" data-testid={`${testidPrefix}-item-row-${i}`}>
          <select value={it.category} onChange={(e) => update(i, { category: e.target.value })} data-testid={`${testidPrefix}-item-category-${i}`} className={inputCls}>
            <option value="">Categoria…</option>
            {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <input value={it.description} onChange={(e) => update(i, { description: e.target.value })}
            placeholder="Descrizione articolo (es. iPhone 14 usato)" data-testid={`${testidPrefix}-item-description-${i}`} className={inputCls} />
          <input value={it.amount} onChange={(e) => update(i, { amount: e.target.value })} inputMode="decimal"
            placeholder="0,00" data-testid={`${testidPrefix}-item-amount-${i}`} className={inputCls} />
          <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} disabled={items.length <= 1}
            data-testid={`${testidPrefix}-item-remove-${i}`}
            className="p-2 text-muted-foreground hover:text-destructive disabled:opacity-30 transition-colors duration-150">
            <X size={16} weight="bold" />
          </button>
        </div>
      ))}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button type="button" onClick={() => setItems([...items, { category: "", description: "", amount: "" }])} data-testid={`${testidPrefix}-item-add`}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md border border-dashed border-border text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors duration-150">
          <Plus size={13} /> Aggiungi articolo
        </button>
        <span className="text-sm">Totale articoli: <b className="text-primary" data-testid={`${testidPrefix}-items-total`}>{eur(total)}</b></span>
      </div>
    </div>
  );
}

export default function PrimaNota() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState({ entrata: [], uscita: [] });
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [type, setType] = useState("entrata");
  const [entryDate, setEntryDate] = useState("");
  const [expandedDesc, setExpandedDesc] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [description, setDescription] = useState("");
  const [receiptType, setReceiptType] = useState("scontrino");
  const [salePrice, setSalePrice] = useState("");
  const [tradeIn, setTradeIn] = useState("");
  const [tradeInNotes, setTradeInNotes] = useState("");
  const [credits, setCredits] = useState([]);
  const [settling, setSettling] = useState(null);
  const [settleAmount, setSettleAmount] = useState("");
  const [settleMethod, setSettleMethod] = useState("contanti");
  const [settleReceipt, setSettleReceipt] = useState("scontrino");
  const [settleSaving, setSettleSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editMethods, setEditMethods] = useState([]);
  const [editAmounts, setEditAmounts] = useState({});
  const [editCategory, setEditCategory] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editReceipt, setEditReceipt] = useState("scontrino");
  const [editSaving, setEditSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [showCatManager, setShowCatManager] = useState(false);
  const [catAmounts, setCatAmounts] = useState({});
  const [pendingSale, setPendingSale] = useState(false);
  const [pendingAmount, setPendingAmount] = useState("");
  const [editItems, setEditItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState(null);
  const [operators, setOperators] = useState([]);
  const [opName, setOpName] = useState(() => localStorage.getItem("active_operator") || "");
  const fileRef = useRef(null);

  const loadCategories = async () => {
    const { data } = await api.get("/categories");
    const grouped = { entrata: [], uscita: [] };
    data.forEach((c) => grouped[c.type].push(c));
    setCategories(grouped);
    return grouped;
  };

  const load = async () => {
    const params = { date_from: dateFrom || undefined, date_to: dateTo || undefined };
    const [e, s, cr] = await Promise.all([
      api.get("/cash", { params }),
      api.get("/cash/summary", { params }),
      api.get("/cash/credits"),
    ]);
    setEntries(e.data);
    setSummary(s.data);
    setCredits(cr.data);
  };

  useEffect(() => {
    loadCategories().catch(() => {});
  }, []);

  useEffect(() => { load().catch(() => {}); }, [dateFrom, dateTo]);

  useEffect(() => {
    api.get("/users/technicians").then((r) => setOperators((r.data || []).filter((o) => !o.generic))).catch(() => {});
    const h = () => setOpName(localStorage.getItem("active_operator") || "");
    window.addEventListener("operator-changed", h);
    return () => window.removeEventListener("operator-changed", h);
  }, []);

  useEffect(() => {
    if (type === "entrata" && selectedCategories.length >= 2 && selectedMethods.length === 1) {
      const tot = selectedCategories.reduce((s, n) => s + (parseFloat(String(catAmounts[n] || "").replace(",", ".")) || 0), 0);
      setAmounts({ [selectedMethods[0]]: tot > 0 ? String(tot).replace(".", ",") : "" });
    }
  }, [type, selectedCategories, catAmounts, selectedMethods]);

  const opRole = (operators.find((o) => o.name === opName)?.role) || (user?.generic ? "collaboratore" : user?.role);
  const opIsAdmin = opRole === "admin";

  const switchType = (t) => {
    setType(t);
    setSelectedCategories([]);
    setCatAmounts({});
    setPendingSale(false);
    setPendingAmount("");
    const valid = methodsForType(t).map((m) => m.id);
    setSelectedMethods((prev) => prev.filter((m) => valid.includes(m)));
    setAmounts((a) => Object.fromEntries(Object.entries(a).filter(([k]) => valid.includes(k))));
  };

  const toggleCategory = (name) => {
    const removing = selectedCategories.includes(name);
    if (type === "uscita") setSelectedCategories(removing ? [] : [name]);
    else setSelectedCategories(removing ? selectedCategories.filter((x) => x !== name) : [...selectedCategories, name]);
    if (removing) setCatAmounts((a) => { const n = { ...a }; delete n[name]; return n; });
    if (name.trim().toLowerCase() === "acc./saldo fattura") setReceiptType("fattura");
  };

  const toggleMethod = (m) => {
    setSelectedMethods((prev) => {
      if (prev.includes(m)) {
        setAmounts((a) => { const n = { ...a }; delete n[m]; return n; });
        return prev.filter((x) => x !== m);
      }
      return [...prev, m];
    });
  };

  const parseUtc = (s) => { if (!s) return null; const d = new Date(/Z|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + "Z"); return isNaN(d) ? null : d; };
  const canEdit = (e) => { const d = parseUtc(e.created_at); return !!d && (Date.now() - d.getTime()) <= 60 * 60 * 1000; };

  const openEdit = (e) => {
    setEditing(e);
    setEditMethods((e.payments || []).map((p) => p.method));
    setEditAmounts(Object.fromEntries((e.payments || []).map((p) => [p.method, String(p.amount).replace(".", ",")])));
    setEditCategory(e.category);
    setEditDescription(e.description || "");
    setEditReceipt(e.receipt_type || "scontrino");
    setEditItems((e.items || []).map((it) => ({ category: it.category, description: it.description || "", amount: String(it.amount).replace(".", ",") })));
  };

  const toggleEditMethod = (m) => {
    setEditMethods((prev) => {
      if (prev.includes(m)) {
        setEditAmounts((a) => { const n = { ...a }; delete n[m]; return n; });
        return prev.filter((x) => x !== m);
      }
      return [...prev, m];
    });
  };

  const confirmEdit = async () => {
    if (!editing) return;
    const invCat = editCategory.trim().toLowerCase() === "acc./saldo fattura";
    const fatSel = editing.type === "entrata" && !editMethods.includes("cash") && editReceipt === "fattura";
    if ((editing.type === "uscita" || invCat || fatSel) && !editDescription.trim()) {
      toast.error("Descrizione obbligatoria");
      return;
    }
    const payments = editMethods
      .map((m) => ({ method: m, amount: parseFloat(String(editAmounts[m] || "").replace(",", ".")) || 0 }))
      .filter((p) => p.amount > 0);
    if (!payments.length) { toast.error("Inserisci almeno un importo valido"); return; }
    const itemsPayload = editing.items?.length > 0
      ? editItems.map((it) => ({ category: it.category, description: it.description.trim(), amount: parseFloat(String(it.amount).replace(",", ".")) || 0 }))
      : undefined;
    if (itemsPayload) {
      if (itemsPayload.some((it) => !it.category || it.amount <= 0)) { toast.error("Ogni articolo deve avere categoria e importo valido"); return; }
      const itSum = itemsPayload.reduce((s, it) => s + it.amount, 0);
      const paySum = payments.reduce((s, p) => s + p.amount, 0);
      if (Math.abs(itSum - paySum) > 0.001) { toast.error("Il totale dei pagamenti deve essere uguale al totale degli articoli"); return; }
    }
    setEditSaving(true);
    try {
      await api.patch(`/cash/${editing.id}`, {
        payments,
        category: editing.items?.length > 0 ? undefined : editCategory,
        description: editDescription.trim(),
        receipt_type: editing.type === "entrata" && !editMethods.includes("cash") ? editReceipt : undefined,
        items: itemsPayload,
      });
      toast.success("Movimento aggiornato");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditSaving(false);
    }
  };

  const total = selectedMethods.reduce((s, m) => s + (parseFloat(String(amounts[m] || "").replace(",", ".")) || 0), 0);
  const articlesTotal = selectedCategories.reduce((s, n) => s + (parseFloat(String(catAmounts[n] || "").replace(",", ".")) || 0), 0);
  const monthOptions = (() => {
    const opts = [];
    const now = new Date();
    const cur = new Date(now.getFullYear(), now.getMonth(), 1);
    const start = new Date(2026, 5, 1);
    for (let d = new Date(cur); d >= start; d.setMonth(d.getMonth() - 1)) {
      const y = d.getFullYear(), m = d.getMonth();
      const first = `${y}-${String(m + 1).padStart(2, "0")}-01`;
      const last = `${y}-${String(m + 1).padStart(2, "0")}-${String(new Date(y, m + 1, 0).getDate()).padStart(2, "0")}`;
      const label = d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
      opts.push({ value: `${first}|${last}`, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return opts;
  })();
  const saleNum = parseFloat(String(salePrice).replace(",", ".")) || 0;
  const tradeNum = parseFloat(String(tradeIn).replace(",", ".")) || 0;
  const saleDue = Math.max(0, saleNum - tradeNum);
  const saleBalance = Math.max(0, saleDue - total);

  const submit = async (ev) => {
    ev.preventDefault();
    const payments = selectedMethods.map((m) => ({
      method: m,
      amount: parseFloat(String(amounts[m] || "0").replace(",", ".")),
    }));
    if (payments.some((p) => !p.amount || p.amount <= 0)) {
      toast.error("Inserisci un importo valido per ogni metodo selezionato");
      return;
    }
    const fatturaSel = type === "entrata" && !selectedMethods.includes("cash") && receiptType === "fattura";
    if ((invoiceCat || fatturaSel) && !description.trim()) {
      toast.error(invoiceCat
        ? "Descrizione obbligatoria: annota quale fattura ha pagato il cliente"
        : "Descrizione obbligatoria: inserisci almeno il numero della fattura");
      return;
    }
    if (type === "uscita" && !description.trim()) {
      toast.error("Descrizione obbligatoria per le uscite: annota cosa hai pagato");
      return;
    }
    if (smartphoneCat) {
      if (!saleNum || saleNum <= 0) {
        toast.error("Inserisci il prezzo dello smartphone");
        return;
      }
      if (total > saleDue + 0.001) {
        toast.error("L'importo pagato supera il totale da pagare (prezzo − permuta)");
        return;
      }
    }
    let itemsPayload;
    if (multiMode) {
      itemsPayload = selectedCategories.map((n) => ({ category: n, description: "", amount: parseFloat(String(catAmounts[n] || "").replace(",", ".")) || 0 }));
      if (itemsPayload.some((it) => it.amount <= 0)) { toast.error("Inserisci un importo valido per ogni articolo"); return; }
      if (!pendingSale && Math.abs(total - articlesTotal) > 0.001) {
        toast.error("Il totale dei pagamenti deve essere uguale al totale degli articoli");
        return;
      }
    }
    if (pendingSale) {
      if (!(pendingTotalVal > 0)) { toast.error("Inserisci l'importo totale della vendita da incassare"); return; }
      if (total > pendingTotalVal + 0.001) { toast.error("L'acconto non può superare il totale della vendita"); return; }
    }
    setSaving(true);
    try {
      const hasCash = selectedMethods.includes("cash");
      await api.post("/cash", {
        type, payments,
        category: multiMode ? "Vendita multipla" : category,
        description,
        items: itemsPayload,
        receipt_type: type === "entrata" && !hasCash ? receiptType : undefined,
        sale_price: smartphoneCat ? saleNum : (pendingSale ? pendingTotalVal : undefined),
        trade_in: smartphoneCat && tradeNum > 0 ? tradeNum : undefined,
        trade_in_notes: smartphoneCat && tradeInNotes.trim() ? tradeInNotes.trim() : undefined,
        operator: localStorage.getItem("active_operator") || undefined,
        date: entryDate || undefined,
      });
      toast.success("Movimento registrato");
      setSelectedMethods([]);
      setAmounts({});
      setDescription("");
      setEntryDate("");
      setSelectedCategories([]);
      setCatAmounts({});
      setPendingSale(false);
      setPendingAmount("");
      setReceiptType("scontrino");
      setSalePrice(""); setTradeIn(""); setTradeInNotes("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await api.post("/categories", { type, name: newCatName.trim() });
      toast.success("Categoria aggiunta");
      await loadCategories();
      setSelectedCategories(type === "uscita" ? [newCatName.trim()] : [...selectedCategories, newCatName.trim()]);
      setNewCatName("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeCategory = async (c) => {
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Categoria rimossa");
      const g = await loadCategories();
      if (selectedCategories.includes(c.name)) {
        setSelectedCategories((prev) => {
          const next = prev.filter((n) => n !== c.name);
          return next.length ? next : (g[type][0] ? [g[type][0].name] : []);
        });
        setCatAmounts((a) => { const n = { ...a }; delete n[c.name]; return n; });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const importFile = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/cash/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importazione completata: ${data.imported} movimenti importati, ${data.skipped} righe saltate`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Importazione fallita"));
    } finally {
      ev.target.value = "";
    }
  };

  const openSettle = (c) => {
    setSettling(c);
    setSettleAmount(String(c.balance_due).replace(".", ","));
    setSettleMethod("contanti");
    setSettleReceipt("scontrino");
  };

  const confirmSettle = async () => {
    const amt = parseFloat(String(settleAmount).replace(",", ".")) || 0;
    if (amt <= 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    setSettleSaving(true);
    try {
      await api.post(`/cash/${settling.id}/settle`, {
        payments: [{ method: settleMethod, amount: amt }],
        receipt_type: settleMethod === "cash" ? undefined : settleReceipt,
      });
      toast.success("Saldo registrato");
      setSettling(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSettleSaving(false);
    }
  };

  const origOf = (e) => entries.find((x) => x.id === e.settlement_for);

  const scrollToEntry = (id) => {
    const el = document.getElementById(`cash-row-${id}`);
    if (!el) {
      toast.error("La vendita originale non è in questa vista: amplia il filtro date");
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-primary");
    setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 2000);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/cash/${id}`);
      toast.success("Movimento eliminato");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportUrl = (kind) => {
    const p = new URLSearchParams();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    return `${API_BASE}/cash/export/${kind}?${p.toString()}`;
  };

  const cats = categories[type] || [];
  const category = selectedCategories[0] || "";
  const multiMode = type === "entrata" && selectedCategories.length >= 2;
  const invoiceCat = !multiMode && category.trim().toLowerCase() === "acc./saldo fattura";
  const smartphoneCat = !multiMode && type === "entrata" && category.trim().toLowerCase() === "smartphone";
  const pendingTotalVal = multiMode ? articlesTotal : (parseFloat(String(pendingAmount).replace(",", ".")) || 0);
  const byCategory = Object.entries(summary?.by_category || {}).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="p-4 md:p-8 space-y-6 md:space-y-8" data-testid="prima-nota-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">
            {isAdmin ? "Entrate / Uscite" : "Movimenti di oggi"}
          </p>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-none">Prima Nota</h1>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <a href={exportUrl("csv")} data-testid="export-csv-button"
               className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm font-semibold hover:bg-accent transition-colors duration-150">
              <FileCsv size={17} /> Export CSV
            </a>
            <a href={exportUrl("pdf")} data-testid="export-pdf-button"
               className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm font-semibold hover:bg-accent transition-colors duration-150">
              <FilePdf size={17} /> Export PDF
            </a>
            <button onClick={() => fileRef.current?.click()} data-testid="import-csv-button"
               className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm font-semibold hover:bg-accent transition-colors duration-150">
              <UploadSimple size={17} /> Importa CSV
            </button>
            <input ref={fileRef} type="file" accept=".csv" onChange={importFile} className="hidden" data-testid="import-csv-file-input" />
          </div>
        )}
      </div>

      {!isAdmin && (
        <p className="text-sm text-muted-foreground border border-border rounded-md bg-card px-4 py-3" data-testid="today-only-note">
          Come collaboratore vedi e registri solo i movimenti del giorno corrente.
        </p>
      )}

      <form onSubmit={submit} className="border border-border rounded-md bg-card" data-testid="cash-entry-form">
        <div className="px-6 py-5 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-heading font-bold tracking-tight">Nuovo movimento</h2>
          <div className="flex items-baseline gap-2.5">
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Totale movimento</span>
            <span className={`font-heading font-extrabold text-xl ${type === "entrata" ? "text-green-500" : "text-red-500"}`} data-testid="payment-total">{eur(total)}</span>
          </div>
        </div>
        <div className="p-6 space-y-8">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-[11px] font-bold flex items-center justify-center shrink-0">1</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Tipo di movimento</span>
            </div>
            <div className="grid grid-cols-2 gap-2 max-w-md">
              <button type="button" onClick={() => switchType("entrata")} data-testid="type-entrata-button"
                className={`py-2.5 rounded-md text-sm font-semibold border transition-colors duration-150 inline-flex items-center justify-center gap-2 ${
                  type === "entrata" ? "bg-green-500/15 border-green-500/50 text-green-500" : "border-border text-muted-foreground hover:text-foreground"}`}>
                <ArrowUpRight size={16} /> Entrata
              </button>
              <button type="button" onClick={() => switchType("uscita")} data-testid="type-uscita-button"
                className={`py-2.5 rounded-md text-sm font-semibold border transition-colors duration-150 inline-flex items-center justify-center gap-2 ${
                  type === "uscita" ? "bg-red-500/15 border-red-500/50 text-red-500" : "border-border text-muted-foreground hover:text-foreground"}`}>
                <ArrowDownRight size={16} /> Uscita
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-[11px] font-bold flex items-center justify-center shrink-0">2</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Categoria{type === "entrata" ? " (anche più di una)" : ""}</span>
              </div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setShowCatManager(!showCatManager)} data-testid="toggle-category-manager-button"
                  className="text-[10px] font-semibold uppercase tracking-wider text-primary hover:opacity-80 transition-opacity duration-150">
                  {showCatManager ? "Chiudi gestione" : "Gestisci"}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="category-buttons">
                {cats.map((c) => (
                  <div key={c.id} className="relative group">
                    <button type="button" onClick={() => toggleCategory(c.name)} data-testid={`category-${c.name.toLowerCase().replace(/\s+/g, "-")}-button`}
                      className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                        selectedCategories.includes(c.name)
                          ? type === "entrata"
                            ? "bg-green-500/20 text-green-500 border-green-500/60"
                            : "bg-red-500/20 text-red-500 border-red-500/60"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                      {c.name}
                    </button>
                    {showCatManager && cats.length > 1 && (
                      <button type="button" onClick={() => removeCategory(c)} data-testid={`remove-category-${c.name.toLowerCase().replace(/\s+/g, "-")}-button`}
                        className="absolute -top-1.5 -right-1.5 w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground flex items-center justify-center">
                        <X size={10} weight="bold" />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => setShowCatManager(true)} data-testid="add-category-button"
                  className="px-3 py-2 rounded-full text-xs font-semibold border border-dashed border-border text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors duration-150">
                  <Plus size={12} /> Nuova
                </button>
            </div>
              {showCatManager && (
                <div className="flex gap-2 mt-3">
                  <input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="Nome nuova categoria"
                    data-testid="new-category-input" className={inputCls} />
                  <button type="button" onClick={addCategory} data-testid="confirm-add-category-button"
                    className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold shrink-0 hover:opacity-90 transition-opacity duration-150">
                    Aggiungi
                  </button>
                </div>
              )}
          </div>

          {multiMode && (
            <div className="border border-primary/40 bg-primary/5 rounded-md p-4" data-testid="multi-sale-section">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Prezzo di ciascun articolo</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl" data-testid="article-amounts">
                {selectedCategories.map((n) => (
                  <div key={n} className="flex items-center gap-3">
                    <span className="text-xs font-semibold w-28 shrink-0 truncate">{n}</span>
                    <input value={catAmounts[n] || ""} onChange={(e) => setCatAmounts({ ...catAmounts, [n]: e.target.value })}
                      inputMode="decimal" placeholder="0,00" data-testid={`article-amount-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="flex-1 min-w-0 px-3.5 py-2.5 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-primary transition-colors duration-150" />
                    <span className="text-xs text-muted-foreground shrink-0">€</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-sm" data-testid="articles-total">Totale articoli: <b className="text-primary">{eur(articlesTotal)}</b></div>
            </div>
          )}

          {smartphoneCat && (
            <div className="border border-primary/40 bg-primary/5 rounded-md p-4 space-y-4" data-testid="smartphone-sale-section">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                <div>
                  <label className={labelCls}>Prezzo smartphone (€) *</label>
                  <input value={salePrice} onChange={(e) => setSalePrice(e.target.value)} inputMode="decimal"
                    placeholder="Es. 1000,00" data-testid="sale-price-input" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Permuta (€)</label>
                  <input value={tradeIn} onChange={(e) => setTradeIn(e.target.value)} inputMode="decimal"
                    placeholder="Es. 200,00" data-testid="trade-in-input" className={inputCls} />
                </div>
              </div>
              <div className="max-w-2xl">
                <label className={labelCls}>Cosa ha permutato (opzionale)</label>
                <input value={tradeInNotes} onChange={(e) => setTradeInNotes(e.target.value)}
                  placeholder="Es. iPhone 8 vetro rotto" data-testid="trade-in-notes-input" className={inputCls} />
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="sale-totals">
                <span>Totale da pagare: <b data-testid="sale-due-total">{eur(saleDue)}</b></span>
                <span className={saleBalance > 0 ? "text-amber-500" : "text-green-500"}>
                  Resta da saldare: <b data-testid="sale-balance-due">{eur(saleBalance)}</b>
                </span>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-[11px] font-bold flex items-center justify-center shrink-0">3</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Metodo di pagamento (anche più di uno)</span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="payment-method-buttons">
              {methodsForType(type).map((m) => (
                <button key={m.id} type="button" onClick={() => toggleMethod(m.id)} data-testid={`method-${m.id}-button`}
                  className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                    selectedMethods.includes(m.id)
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                  {m.label}
                </button>
              ))}
            </div>
            {selectedMethods.length > 0 && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl" data-testid="payment-amounts">
                {selectedMethods.map((m) => (
                  <div key={m} className="flex items-center gap-3">
                    <span className="text-xs font-semibold w-28 shrink-0">{methodLabel(m)}</span>
                    <input value={amounts[m] || ""} onChange={(e) => setAmounts({ ...amounts, [m]: e.target.value })}
                      inputMode="decimal" placeholder="0,00" data-testid={`amount-${m}-input`}
                      className="flex-1 min-w-0 px-3.5 py-2.5 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-primary transition-colors duration-150" />
                    <span className="text-xs text-muted-foreground shrink-0">€</span>
                  </div>
                ))}
              </div>
            )}
            {multiMode && !pendingSale && selectedMethods.length > 0 && Math.abs(total - articlesTotal) > 0.001 && (
              <p className="mt-3 max-w-2xl text-[11px] text-amber-500 border border-amber-500/30 bg-amber-500/10 rounded-md px-3 py-2" data-testid="multi-sale-mismatch">
                Il totale dei pagamenti ({eur(total)}) non corrisponde al totale degli articoli ({eur(articlesTotal)}).
              </p>
            )}
            {type === "entrata" && !smartphoneCat && (
              <div className="mt-4 max-w-2xl border border-amber-500/40 rounded-md p-3.5" data-testid="pending-sale-box">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={pendingSale} onChange={(e) => setPendingSale(e.target.checked)}
                    data-testid="pending-sale-toggle" className="w-4 h-4 accent-amber-500" />
                  <span className="text-xs font-semibold text-amber-500">Il cliente non paga ora — registra come "da incassare"</span>
                </label>
                {pendingSale && (
                  <div className="mt-3 space-y-2">
                    <p className="text-[11px] text-muted-foreground" data-testid="pending-sale-note">
                      Non conta nell'incassato del giorno: resta in "Crediti da incassare" finché non registri il saldo. Se il cliente lascia un acconto, indicalo sopra con i metodi di pagamento.
                    </p>
                    {multiMode ? (
                      <p className="text-[11px]" data-testid="pending-total-auto">Totale vendita da incassare: <b className="text-amber-500">{eur(articlesTotal)}</b> (= totale articoli)</p>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold w-40 shrink-0">Importo totale vendita</span>
                        <input value={pendingAmount} onChange={(e) => setPendingAmount(e.target.value)} inputMode="decimal" placeholder="0,00"
                          data-testid="pending-total-input"
                          className="flex-1 min-w-0 px-3.5 py-2.5 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-amber-500 transition-colors duration-150" />
                        <span className="text-xs text-muted-foreground shrink-0">€</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {selectedMethods.includes("cash") && (
              <p className="mt-3 max-w-2xl text-[11px] text-amber-500 border border-amber-500/30 bg-amber-500/10 rounded-md px-3 py-2" data-testid="cash-warning-note">
                Il metodo "Cash" non viene contabilizzato in prima nota: resta solo come traccia interna di controllo.
              </p>
            )}
          </div>

          {type === "entrata" && (
            <div data-testid="receipt-type-section">
              <div className="flex items-center gap-2.5 mb-3">
                <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-[11px] font-bold flex items-center justify-center shrink-0">4</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Documento</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={selectedMethods.includes("cash") || invoiceCat} onClick={() => setReceiptType("scontrino")}
                  data-testid="receipt-scontrino-button"
                  className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                    selectedMethods.includes("cash") || invoiceCat
                      ? "border-border text-muted-foreground/50 cursor-not-allowed"
                      : receiptType === "scontrino"
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                  Scontrino
                </button>
                <button type="button" disabled={selectedMethods.includes("cash")} onClick={() => { setReceiptType("fattura"); if (!description.trim()) setDescription("Fattura N. "); }}
                  data-testid="receipt-fattura-button"
                  className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                    selectedMethods.includes("cash")
                      ? "border-border text-muted-foreground/50 cursor-not-allowed"
                      : receiptType === "fattura"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                  Fattura
                </button>
              </div>
              {selectedMethods.includes("cash") && (
                <p className="text-[11px] text-muted-foreground mt-2" data-testid="receipt-cash-note">
                  Non disponibile per il metodo "Cash": il movimento non viene contabilizzato.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-5 border-t border-border flex flex-col md:flex-row md:items-end gap-4">
          {opIsAdmin && (
            <div className="shrink-0 md:w-44">
              <label className={labelCls}>Data movimento <span className="normal-case tracking-normal">(default: oggi)</span></label>
              <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                data-testid="cash-entry-date" className={inputCls} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <label className={labelCls}>
              Descrizione {invoiceCat
                ? <span className="text-amber-500">* obbligatoria — quale fattura ha pagato il cliente?</span>
                : type === "entrata" && receiptType === "fattura" && !selectedMethods.includes("cash")
                  ? <span className="text-amber-500">* obbligatoria — inserisci almeno il numero della fattura</span>
                  : type === "uscita"
                    ? <span className="text-amber-500">* obbligatoria — cosa stai pagando?</span>
                    : "(opzionale)"}
            </label>
            <input value={description} onChange={(e) => setDescription(e.target.value)}
              data-testid="cash-description-input" className={inputCls}
              placeholder={invoiceCat ? "Es. Saldo fattura n. 123 — Mario Rossi"
                : type === "entrata" && receiptType === "fattura" && !selectedMethods.includes("cash") ? "Es. Fattura n. 123 — Mario Rossi"
                : type === "uscita" ? "Es. Batteria Samsung S24 — fornitore" : "Es. Vendita notebook HP"} />
          </div>
          <button type="submit" disabled={saving || (!pendingSale && selectedMethods.length === 0) || selectedCategories.length === 0 || (multiMode && !pendingSale && Math.abs(total - articlesTotal) > 0.001) || (smartphoneCat && !(saleNum > 0)) || (pendingSale && !(pendingTotalVal > 0))} data-testid="cash-submit-button"
            className="w-full md:w-64 shrink-0 py-3.5 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-40 transition-opacity duration-150">
            {saving ? "Salvataggio..." : "Registra movimento"}
          </button>
        </div>
      </form>

      {credits.length > 0 && (
        <div className="border border-amber-500/40 bg-amber-500/10 rounded-md p-5" data-testid="credits-section">
          <h2 className="font-heading font-bold text-amber-500 flex items-center gap-2 mb-3">
            <Warning size={18} weight="duotone" /> Crediti da incassare ({credits.length})
          </h2>
          <ul className="space-y-2">
            {credits.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 flex-wrap border border-border bg-card rounded-md px-4 py-3" data-testid={`credit-${c.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{c.description || c.category}</p>
                  <p className="text-xs text-muted-foreground">
                    {fmtShortDate(c.date)} · vendita {eur(c.sale_price)}{c.trade_in ? ` − permuta ${eur(c.trade_in)}` : ""}
                    {c.trade_in_notes ? ` (${c.trade_in_notes})` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-heading font-extrabold text-amber-500" data-testid={`credit-balance-${c.id}`}>{eur(c.balance_due)}</span>
                  <button onClick={() => openSettle(c)} data-testid={`settle-button-${c.id}`}
                    className="px-3.5 py-2 rounded-md bg-green-600 text-white text-xs font-bold hover:bg-green-500 transition-colors duration-150">
                    Registra saldo
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border border-border rounded-md bg-card overflow-hidden overflow-x-auto" data-testid="cash-register">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-heading font-bold tracking-tight">Storico movimenti</h2>
            <span className="text-xs text-muted-foreground" data-testid="register-count">{entries.length} movimenti</span>
          </div>
          {isAdmin && (
            <div className="flex items-end gap-2 flex-wrap w-full sm:w-auto" data-testid="register-date-filters">
              <div className="flex-1 min-w-[130px] sm:flex-none">
                <label className={labelCls}>Dal</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="filter-date-from" className={inputCls} />
              </div>
              <div className="flex-1 min-w-[130px] sm:flex-none">
                <label className={labelCls}>Al</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  data-testid="filter-date-to" className={inputCls} />
              </div>
              <select
                value={monthOptions.some((o) => o.value === `${dateFrom}|${dateTo}`) ? `${dateFrom}|${dateTo}` : ""}
                onChange={(e) => { if (!e.target.value) return; const [f, t] = e.target.value.split("|"); setDateFrom(f); setDateTo(t); }}
                data-testid="filter-month-select"
                className={`${inputCls} sm:w-48`}>
                <option value="">Seleziona mese…</option>
                {monthOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
        </div>
        <table className="w-full text-sm" data-testid="cash-table">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="px-2 py-3 text-[10px] uppercase tracking-normal sm:tracking-[0.15em] text-muted-foreground font-semibold">Data</th>
              <th className="px-2 sm:px-4 py-3 text-[10px] uppercase tracking-normal sm:tracking-[0.15em] text-muted-foreground font-semibold">Tipo</th>
              <th className="px-2 sm:px-4 py-3 text-[10px] uppercase tracking-normal sm:tracking-[0.15em] text-muted-foreground font-semibold">Categoria</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold hidden md:table-cell">Descrizione</th>
              <th className="px-2 py-3 text-[10px] uppercase tracking-normal sm:tracking-[0.15em] text-muted-foreground font-semibold">Pagamento</th>
              <th className="px-2 sm:px-4 py-3 text-[10px] uppercase tracking-normal sm:tracking-[0.15em] text-muted-foreground font-semibold text-right">Importo</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold text-right hidden lg:table-cell">Contabilizzato</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold hidden lg:table-cell">Inserito da</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {entries.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-16 text-center text-muted-foreground" data-testid="cash-empty">Nessun movimento nel periodo selezionato</td></tr>
            )}
            {entries.map((e) => (
              <Fragment key={e.id}>
              <tr id={`cash-row-${e.id}`} className="hover:bg-accent/40 transition-colors duration-150" data-testid={`cash-row-${e.id}`}>
                <td className="px-2 py-3.5 whitespace-nowrap text-xs font-semibold" data-testid={`cash-date-${e.id}`}>{fmtShortDate(e.date)}</td>
                <td className="px-2 sm:px-4 py-3.5">
                  <span className={`inline-flex items-center gap-1 text-xs font-bold uppercase ${e.type === "entrata" ? "text-green-500" : "text-red-500"}`}>
                    {e.type === "entrata" ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
                    <span className="hidden sm:inline">{e.type}</span>
                  </span>
                </td>
                <td className="px-2 sm:px-4 py-3.5">{e.category}
                  {e.balance_due > 0 && (
                    <span className="block text-[10px] font-bold text-amber-500" data-testid={`pending-badge-${e.id}`}>Da incassare: {eur(e.balance_due)}</span>
                  )}
                  {e.items?.length > 0 && (
                    <button type="button" onClick={() => setExpandedItems(expandedItems === e.id ? null : e.id)} data-testid={`items-toggle-${e.id}`}
                      className="block text-[10px] font-bold text-primary hover:opacity-80 transition-opacity duration-150">
                      {e.items.length} articoli {expandedItems === e.id ? "▲" : "▼"}
                    </button>
                  )}
                  {e.sale_price != null && (
                    <span className="block text-[10px] text-muted-foreground" data-testid={`sale-info-${e.id}`}>
                      vendita {eur(e.sale_price)}{e.trade_in ? ` − permuta ${eur(e.trade_in)}` : ""}
                      {(e.balance_due || 0) > 0 ? ` · saldo ${eur(e.balance_due)}` : " · saldato"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3.5 max-w-[200px] hidden md:table-cell">
                  <span onClick={() => setExpandedDesc(expandedDesc === e.id ? null : e.id)}
                    title={expandedDesc === e.id ? "Click per ridurre" : "Click per leggere tutto"}
                    data-testid={`cash-description-${e.id}`}
                    className={`block cursor-pointer hover:text-primary transition-colors duration-150 ${expandedDesc === e.id ? "whitespace-normal break-words" : "truncate"}`}>{e.description || "—"}</span>
                  {e.settlement_for && (
                    <button type="button" onClick={() => scrollToEntry(e.settlement_for)} data-testid={`settlement-link-${e.id}`}
                      className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary/15 text-primary hover:bg-primary/25 transition-colors duration-150">
                      <ArrowBendUpLeft size={11} weight="bold" /> vendita{origOf(e) ? ` del ${fmtShortDate(origOf(e).date)}` : ""}
                    </button>
                  )}
                </td>
                <td className="px-2 py-3.5">
                  <div className="flex flex-col gap-0.5">
                    {(e.payments || []).map((p, i) => (
                      <span key={i} className="text-[11px] sm:text-xs whitespace-nowrap">
                        {methodLabel(p.method)}<b className="hidden sm:inline">: {eur(p.amount)}</b>
                      </span>
                    ))}
                    {e.type === "entrata" && e.receipt_type && (
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`receipt-type-${e.id}`}>
                        {e.receipt_type === "fattura" ? "Fattura" : "Scontrino"}
                      </span>
                    )}
                  </div>
                </td>
                <td className={`px-2 sm:px-4 py-3.5 text-right font-semibold whitespace-nowrap ${e.type === "entrata" ? "text-green-500" : "text-red-500"}`}>
                  {e.type === "entrata" ? "+" : "−"} {eur(e.amount)}
                </td>
                <td className="px-4 py-3.5 text-right whitespace-nowrap hidden lg:table-cell" data-testid={`official-amount-${e.id}`}>
                  {eur(e.official_amount)}
                  {e.cash_amount > 0 && <span className="block text-[10px] text-amber-500">cash: {eur(e.cash_amount)}</span>}
                </td>
                <td className="px-4 py-3.5 text-xs text-muted-foreground whitespace-nowrap hidden lg:table-cell">{e.created_by}</td>
                <td className="px-2 sm:px-4 py-3.5 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {e.balance_due > 0 && (
                      <button onClick={() => openSettle(e)} data-testid={`cash-settle-${e.id}`} title="Registra l'incasso del saldo"
                        className="px-2.5 py-1 rounded-md bg-green-600 text-white text-[10px] font-bold hover:bg-green-500 transition-colors duration-150">
                        Incassa
                      </button>
                    )}
                    {(opIsAdmin || canEdit(e)) && (
                      <button onClick={() => openEdit(e)} data-testid={`cash-edit-${e.id}`} title={opIsAdmin ? "Modifica (admin: sempre consentita)" : "Modifica (consentita entro 60 minuti dall'inserimento)"}
                        className="text-muted-foreground hover:text-primary transition-colors duration-150">
                        <Pencil size={16} />
                      </button>
                    )}
                    {(opIsAdmin || canEdit(e)) && (
                      <button onClick={() => remove(e.id)} data-testid={`cash-delete-${e.id}`} title={opIsAdmin ? "Elimina (admin: sempre consentito)" : "Elimina (consentito entro 60 minuti dall'inserimento)"}
                        className="text-muted-foreground hover:text-destructive transition-colors duration-150">
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {expandedItems === e.id && e.items?.length > 0 && (
                <tr data-testid={`items-detail-${e.id}`}>
                  <td colSpan={9} className="px-4 py-3 bg-accent/30">
                    <div className="space-y-1 max-w-lg">
                      {e.items.map((it, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 text-xs" data-testid={`items-detail-${e.id}-row-${i}`}>
                          <span><b>{it.category}</b>{it.description ? ` — ${it.description}` : ""}</span>
                          <b>{eur(it.amount)}</b>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {summary && (
        <section className="border border-border rounded-md bg-card p-6 space-y-6" data-testid="recap-section">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Totali del periodo</p>
              <h2 className="font-heading text-xl font-bold tracking-tight">Ricapitolo{isAdmin ? "" : " di oggi"}</h2>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border rounded-md overflow-hidden" data-testid="cash-summary">
            <div className="bg-background p-5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Entrate</p>
              <p className="font-heading text-xl md:text-2xl font-extrabold text-green-500" data-testid="summary-entrate">{eur(summary.entrate)}</p>
              <p className="text-xs text-muted-foreground mt-1" data-testid="summary-entrate-con-cash">con cash: {eur(summary.entrate + (summary.by_method?.cash?.entrate || 0))}</p>
              {(summary.da_incassare || 0) > 0 && (
                <p className="text-xs font-semibold text-amber-500 mt-1" data-testid="summary-da-incassare">di cui da incassare: {eur(summary.da_incassare)}</p>
              )}
            </div>
            <div className="bg-background p-5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Uscite</p>
              <p className="font-heading text-xl md:text-2xl font-extrabold text-red-500" data-testid="summary-uscite">{eur(summary.uscite)}</p>
              <p className="text-xs text-muted-foreground mt-1" data-testid="summary-uscite-con-cash">con cash: {eur(summary.uscite + (summary.by_method?.cash?.uscite || 0))}</p>
            </div>
            <div className="bg-background p-5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Saldo</p>
              <p className={`font-heading text-xl md:text-2xl font-extrabold ${summary.saldo >= 0 ? "text-green-500" : "text-red-500"}`} data-testid="summary-saldo">{eur(summary.saldo)}</p>
              <p className="text-xs text-muted-foreground mt-1" data-testid="summary-saldo-con-cash">con cash: {eur(summary.saldo + (summary.by_method?.cash?.entrate || 0) - (summary.by_method?.cash?.uscite || 0))}</p>
            </div>
            <div className="bg-background p-5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Cash (non contab.)</p>
              <p className="font-heading text-xl md:text-2xl font-extrabold text-amber-500" data-testid="summary-cash-entrati">{eur(summary.by_method?.cash?.entrate || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">solo cash entrati</p>
            </div>
            <div className="bg-background p-5 col-span-2 md:col-span-1">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Cassa del giorno</p>
              <p className="font-heading text-xl md:text-2xl font-extrabold text-primary" data-testid="summary-cassa-giorno">{eur(
                (summary.by_method?.contanti?.entrate || 0) + (summary.by_method?.cash?.entrate || 0)
                - (summary.by_method?.contanti?.uscite || 0) - (summary.by_method?.cash?.uscite || 0)
              )}</p>
              <p className="text-xs text-muted-foreground mt-1">Contanti + Cash, al netto delle uscite</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Per metodo di pagamento</p>
              <div className="border border-border rounded-md divide-y divide-border" data-testid="methods-breakdown">
                {METHODS.map((m) => {
                  const v = summary.by_method?.[m.id] || { entrate: 0, uscite: 0 };
                  return (
                    <div key={m.id} className="px-4 py-3 flex items-center justify-between flex-wrap gap-x-3 gap-y-1" data-testid={`method-total-${m.id}`}>
                      <span className="text-sm font-medium">
                        {m.label}
                        {m.id === "cash" && <span className="text-[10px] text-amber-500 ml-1.5">(non contabilizzato)</span>}
                      </span>
                      <span className="text-sm whitespace-nowrap">
                        <b className="text-green-500">+{eur(v.entrate)}</b>
                        <span className="text-muted-foreground mx-1.5">/</span>
                        <b className="text-red-500">−{eur(v.uscite)}</b>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Per categoria</p>
              <div className="border border-border rounded-md divide-y divide-border" data-testid="categories-breakdown">
                {byCategory.length === 0 && (
                  <p className="px-4 py-6 text-sm text-muted-foreground text-center">Nessun movimento nel periodo</p>
                )}
                {byCategory.map(([k, v]) => {
                  const [t, c] = k.split(":");
                  return (
                    <div key={k} className="px-4 py-3 flex items-center justify-between" data-testid={`category-total-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                      <span className="text-sm">
                        {c}{" "}
                        <span className={`text-[10px] font-bold uppercase ${t === "entrata" ? "text-green-500" : "text-red-500"}`}>{t}</span>
                      </span>
                      <b className="text-sm">{eur(v)}</b>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      <Dialog open={!!settling} onOpenChange={(v) => !v && setSettling(null)}>
        <DialogContent className="max-w-md" data-testid="settle-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-extrabold tracking-tight">Registra saldo</DialogTitle>
            <DialogDescription className="sr-only">Registra il saldo incassato per questa vendita smartphone</DialogDescription>
          </DialogHeader>
          {settling && (
            <div className="space-y-5 mt-2">
              <div className="border border-border rounded-md px-4 py-3">
                <p className="text-sm font-bold">{settling.description || settling.category}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Vendita {eur(settling.sale_price)}{settling.trade_in ? ` − permuta ${eur(settling.trade_in)}` : ""}
                </p>
                <p className="font-heading text-2xl font-extrabold mt-2 text-amber-500" data-testid="settle-balance">{eur(settling.balance_due)}</p>
              </div>
              <div>
                <label className={labelCls}>Importo incassato ora (€) *</label>
                <input value={settleAmount} onChange={(e) => setSettleAmount(e.target.value)} inputMode="decimal"
                  data-testid="settle-amount-input" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Metodo di pagamento</label>
                <div className="flex flex-wrap gap-2">
                  {[["contanti", "Contanti"], ["carta", "Carta"], ["bonifico", "Bonifico"], ["assegno", "Assegno"], ["finanziamento", "Finanziamento"], ["cash", "Cash"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setSettleMethod(v)} data-testid={`settle-method-${v}`}
                      className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                        settleMethod === v
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div data-testid="settle-receipt-section">
                <label className={labelCls}>Documento</label>
                <div className="flex flex-wrap gap-2">
                  {[["scontrino", "Scontrino"], ["fattura", "Fattura"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => settleMethod !== "cash" && setSettleReceipt(v)} data-testid={`settle-receipt-${v}`}
                      className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                        settleMethod === "cash"
                          ? "border-border text-muted-foreground/50 cursor-not-allowed"
                          : settleReceipt === v
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={confirmSettle} disabled={settleSaving} data-testid="confirm-settle-button"
                className="w-full py-3 rounded-md bg-green-600 text-white font-semibold text-sm hover:bg-green-500 disabled:opacity-50 transition-colors duration-150">
                {settleSaving ? "Registrazione..." : "Registra saldo"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="edit-entry-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-extrabold tracking-tight">Modifica movimento</DialogTitle>
            <DialogDescription className="sr-only">Modifica il movimento entro 60 minuti dall'inserimento</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-5 mt-2">
              <p className="text-xs text-muted-foreground">
                {opIsAdmin ? "Sei admin: modifica sempre consentita" : "Consentita entro 60 minuti dall'inserimento"} · tipo: <b className="uppercase">{editing.type}</b>
              </p>
              <div>
                <label className={labelCls}>Metodi di pagamento *</label>
                <div className="flex flex-wrap gap-2">
                  {methodsForType(editing.type).map((m) => (
                    <button key={m.id} type="button" onClick={() => toggleEditMethod(m.id)} data-testid={`edit-method-${m.id}`}
                      className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                        editMethods.includes(m.id)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              {editMethods.map((m) => (
                <div key={m}>
                  <label className={labelCls}>{methodLabel(m)} (€) *</label>
                  <input value={editAmounts[m] || ""} onChange={(e) => setEditAmounts({ ...editAmounts, [m]: e.target.value })} inputMode="decimal"
                    data-testid={`edit-amount-${m}`} className={inputCls} />
                </div>
              ))}
              {editing.items?.length > 0 ? (
                <div>
                  <label className={labelCls}>Articoli *</label>
                  <SaleItemsEditor items={editItems} setItems={setEditItems} categories={categories[editing.type] || []} testidPrefix="edit" />
                </div>
              ) : (
                <div>
                  <label className={labelCls}>Categoria *</label>
                  <select value={editCategory} onChange={(e) => setEditCategory(e.target.value)} data-testid="edit-category-select" className={inputCls}>
                    {(categories[editing.type] || []).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
              )}
              {editing.type === "entrata" && (
                <div data-testid="edit-receipt-section">
                  <label className={labelCls}>Documento</label>
                  <div className="flex flex-wrap gap-2">
                    {[["scontrino", "Scontrino"], ["fattura", "Fattura"]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => !editMethods.includes("cash") && setEditReceipt(v)} data-testid={`edit-receipt-${v}`}
                        className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                          editMethods.includes("cash")
                            ? "border-border text-muted-foreground/50 cursor-not-allowed"
                            : editReceipt === v
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className={labelCls}>
                  Descrizione {(editing.type === "uscita" || (editing.type === "entrata" && editReceipt === "fattura" && !editMethods.includes("cash")) || editCategory.trim().toLowerCase() === "acc./saldo fattura") ? "* obbligatoria" : "(opzionale)"}
                </label>
                <input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} data-testid="edit-description-input" className={inputCls} />
              </div>
              <button onClick={confirmEdit} disabled={editSaving} data-testid="confirm-edit-button"
                className="w-full py-3 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-opacity duration-150">
                {editSaving ? "Salvataggio..." : "Salva modifiche"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
